"use client"
import { useState } from "react"
import { Search, FileText, CheckCircle2, File as FileIcon, Presentation } from "lucide-react"
import { Modal } from "@/components/ui/modal"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { useGetKnowledgeBaseFilesQuery, type KnowledgeBaseFile } from "@/app/store/api/knowledgeBaseApi"
const formatDate = (d: string) => new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

interface KnowledgeBaseModalProps {
    isOpen: boolean
    onClose: () => void
    onImport: (files: KnowledgeBaseFile[]) => void
    maxSelections?: number
}

export function KnowledgeBaseModal({ isOpen, onClose, onImport, maxSelections = 3 }: KnowledgeBaseModalProps) {
    const { data: filesData, isLoading } = useGetKnowledgeBaseFilesQuery(undefined, {
        skip: !isOpen,
        refetchOnMountOrArgChange: true,
    })
    const [searchQuery, setSearchQuery] = useState("")
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
    const [isImporting, setIsImporting] = useState(false)

    const files = filesData?.data || []

    const filteredFiles = files.filter(f =>
        f.fileName?.toLowerCase().includes(searchQuery.toLowerCase()) ?? true
    )

    const handleToggle = (id: string) => {
        const newSet = new Set(selectedIds)
        if (newSet.has(id)) {
            newSet.delete(id)
        } else {
            if (newSet.size >= maxSelections) return
            newSet.add(id)
        }
        setSelectedIds(newSet)
    }

    const handleImport = () => {
        const selectedFiles = files.filter(f => selectedIds.has(f._id))
        onImport(selectedFiles)
        onClose()
        setTimeout(() => setSelectedIds(new Set()), 300)
    }

    const getFileIcon = (fileType: string) => {
        const type = fileType.toLowerCase()
        if (type.includes('pdf')) return <FileText className="h-6 w-6 text-red-500" />
        if (type.includes('doc') || type.includes('docx')) return <FileText className="h-6 w-6 text-blue-600" />
        if (type.includes('ppt') || type.includes('pptx') || type.includes('presentation')) return <Presentation className="h-6 w-6 text-orange-500" />
        return <FileIcon className="h-6 w-6 text-slate-400" />
    }

    return (
        <Modal isOpen={isOpen} onClose={onClose} className="sm:max-w-[600px] p-0">
            <div className="flex items-center justify-between p-4 border-b border-slate-100">
                <h2 className="text-lg font-bold text-slate-800">Knowledge Base</h2>
            </div>

            <div className="p-4 bg-slate-50 border-b border-slate-100">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                        placeholder="Search documents..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9 bg-white border-slate-200 focus-visible:ring-blue-500"
                    />
                </div>
            </div>

            <div className="max-h-[400px] overflow-y-auto p-2">
                {isLoading ? (
                    <div className="flex items-center justify-center py-10">
                        <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                    </div>
                ) : filteredFiles.length === 0 ? (
                    <div className="text-center py-10 px-4">
                        <FileIcon className="w-10 h-10 mx-auto text-slate-300 mb-2" />
                        <h3 className="text-sm font-medium text-slate-700">No documents found</h3>
                        <p className="text-xs text-slate-500 mt-1">
                            {searchQuery ? "Try a different search term" : "Upload documents in the Knowledge Base tab first"}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-1">
                        {filteredFiles.map((file) => {
                            const isSelected = selectedIds.has(file._id)
                            const isMaxReached = selectedIds.size >= maxSelections && !isSelected
                            return (
                                <div
                                    key={file._id}
                                    className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors ${
                                        isSelected ? 'bg-blue-50 border-blue-200' : 'hover:bg-slate-50 border-transparent'
                                    } border ${isMaxReached ? 'opacity-50 cursor-not-allowed' : ''}`}
                                    onClick={() => !isMaxReached && handleToggle(file._id)}
                                >
                                    <div className="p-2 bg-white rounded-lg shadow-sm border border-slate-100 flex-shrink-0">
                                        {getFileIcon(file.fileType)}
                                    </div>

                                    <div className="flex-1 min-w-0">
                                        <h4 className={`text-sm font-semibold truncate ${isSelected ? 'text-blue-700' : 'text-slate-700'}`}>
                                            {file.fileName}
                                        </h4>
                                        <div className="flex items-center gap-2 mt-0.5 text-xs text-slate-500">
                                            <span>{formatDate(file.createdAt)}</span>
                                        </div>
                                    </div>

                                    <div className="px-2">
                                        <div className={`w-5 h-5 rounded-full flex items-center justify-center transition-colors ${
                                            isSelected ? 'bg-blue-600' : 'border-2 border-slate-300'
                                        }`}>
                                            {isSelected && <CheckCircle2 className="w-4 h-4 text-white" />}
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                    </div>
                )}
            </div>

            <div className="p-4 border-t border-slate-100 flex items-center justify-between bg-white">
                <span className="text-sm font-medium text-slate-600">
                    {selectedIds.size} / {maxSelections} selected
                </span>
                <div className="flex items-center gap-2">
                    <Button variant="outline" className="cursor-pointer" onClick={onClose}>Cancel</Button>
                    <Button
                        className="bg-blue-600 cursor-pointer hover:bg-blue-700 text-white"
                        disabled={selectedIds.size === 0 || isImporting}
                        onClick={handleImport}
                    >
                        {isImporting ? (
                            <div className="flex items-center gap-2">
                                <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                Importing...
                            </div>
                        ) : (
                            "Import Selected"
                        )}
                    </Button>
                </div>
            </div>
        </Modal>
    )
}
